import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Coffee, ShoppingBag, Utensils } from 'lucide-react';
import { MenuData, MenuItem, ProductGroup } from '../types';
import { useLanguage } from '../context/LanguageContext';
import MenuItemCard from './MenuItemCard';

interface EnhancedMenuContainerProps {
  menu: MenuData;
}

interface GroupedItems {
  items: MenuItem[];
  categoryId: string;
  categoryName: string;
  mainCategoryId: string;
}

const EnhancedMenuContainer: React.FC<EnhancedMenuContainerProps> = ({ menu }) => {
  const { t } = useLanguage();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [groupedItemsList, setGroupedItemsList] = useState<GroupedItems[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const mainCategoriesRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const contentRef = useRef<HTMLDivElement>(null);

  const { productGroups = [], products = [] as MenuItem[], storeCurrency = "₺" } = menu || {};

  const mainCategories = productGroups.filter(
    category => !category.hide && category.parentID === "0"
  ).sort((a, b) => (a.order || 0) - (b.order || 0));

  const getSubcategories = (parentId: string): ProductGroup[] => {
    return productGroups.filter(
      category => !category.hide && category.parentID === parentId
    ).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const getDirectItems = (categoryId: string): MenuItem[] => {
    return products.filter(item => item.group === categoryId)
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined && a.order !== b.order) {
          return (a.order || 0) - (b.order || 0);
        }
        return a.name.localeCompare(b.name);
      });
  };

  const getMainCategoryId = (categoryId: string): string => {
    const category = productGroups.find(group => group.id === categoryId);
    if (!category) return categoryId;
    return category.parentID === "0" ? categoryId : getMainCategoryId(category.parentID);
  };

  // Tüm ürünleri gruplandır
  useEffect(() => {
    const groups: GroupedItems[] = [];
    mainCategories.forEach(mainCategory => {
      const mainCategoryItems = getDirectItems(mainCategory.id);
      if (mainCategoryItems.length > 0) {
        groups.push({
          items: mainCategoryItems,
          categoryId: mainCategory.id,
          categoryName: mainCategory.name,
          mainCategoryId: mainCategory.id
        });
      }

      const subcategories = getSubcategories(mainCategory.id);
      subcategories.forEach(subcategory => {
        const subcategoryItems = getDirectItems(subcategory.id);
        if (subcategoryItems.length > 0) {
          groups.push({
            items: subcategoryItems,
            categoryId: subcategory.id,
            categoryName: subcategory.name,
            mainCategoryId: mainCategory.id
          });
        }
      });
    });
    setGroupedItemsList(groups);

    if (mainCategories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(mainCategories[0].id);
    }
  }, [productGroups, products]);

  useEffect(() => {
    if (searchActive || groupedItemsList.length === 0) return;

    const handleScroll = () => {
      if (!contentRef.current || !mainCategoriesRef.current) return;

      const viewportHeight = contentRef.current.clientHeight;
      const scrollTop = contentRef.current.scrollTop;
      const centerPosition = scrollTop + (viewportHeight / 2);

      let closestSection: { id: string; distance: number } | null = null;

      sectionsRef.current.forEach((section: HTMLDivElement, categoryId: string) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionCenter = sectionTop + (sectionHeight / 2);
        const distance = Math.abs(centerPosition - sectionCenter);

        if (!closestSection || distance < closestSection.distance) {
          const group = groupedItemsList.find((g: GroupedItems) => g.categoryId === categoryId);
          if (group) {
            closestSection = {
              id: group.mainCategoryId,
              distance: distance
            };
          }
        }
      });

      if (closestSection) {
        const sectionWithId = closestSection as { id: string; distance: number };
        const activeCategoryId: string = sectionWithId.id;
        if (activeCategoryId !== selectedCategoryId) {
          setSelectedCategoryId(activeCategoryId);

          const categoryElement = mainCategoriesRef.current.querySelector(
            `[data-category-id="${activeCategoryId}"]`
          );
          if (categoryElement instanceof HTMLElement) {
            const containerLeft = mainCategoriesRef.current.getBoundingClientRect().left;
            const elementLeft = categoryElement.getBoundingClientRect().left;
            const scrollPosition = elementLeft - containerLeft + mainCategoriesRef.current.scrollLeft - 16;

            mainCategoriesRef.current.scrollTo({
              left: scrollPosition,
              behavior: 'smooth'
            });
          }
        }
      }

      setShowScrollTop(scrollTop > 300);
    };

    const currentContentRef = contentRef.current;
    if (currentContentRef) {
      currentContentRef.addEventListener('scroll', handleScroll);
      return () => currentContentRef.removeEventListener('scroll', handleScroll);
    }
  }, [groupedItemsList, searchActive, selectedCategoryId]);

  const handleCategorySelect = (categoryId: string): void => {
    setSelectedCategoryId(categoryId);
    setSearchQuery('');

    const firstSection = groupedItemsList.find(group => group.mainCategoryId === categoryId);
    if (firstSection && contentRef.current) {
      const sectionElement = sectionsRef.current.get(firstSection.categoryId);
      if (sectionElement) {
        // Sticky başlığın yüksekliğini hesaplayalım
        const headerElement = sectionElement.querySelector('.sticky');
        const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
        
        // Offset değerini arttıralım (başlık yüksekliği + 50px)
        contentRef.current.scrollTo({
          top: sectionElement.offsetTop - (headerHeight + 150),
          behavior: 'smooth'
        });
      }
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
    if (e.target.value) {
      setSearchActive(true);
    } else {
      setSearchActive(false);
    }
  };

  const clearSearch = (): void => {
    setSearchQuery('');
    if (mainCategories.length > 0) {
      setSelectedCategoryId(mainCategories[0].id);
    }
  };

  const scrollToTop = (): void => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const getCategoryIcon = (name: string) => {
    if (!name) return <Utensils className="h-4 w-4 mr-2" />;
    name = name.toLowerCase();
    if (name.includes('içecek') || name.includes('kahve') || name.includes('çay') || 
        name.includes('drink') || name.includes('coffee') || name.includes('tea')) {
      return <Coffee className="h-4 w-4 mr-2" />;
    } else if (name.includes('tatlı') || name.includes('pasta') || 
               name.includes('dessert') || name.includes('cake')) {
      return <ShoppingBag className="h-4 w-4 mr-2" />;
    } else {
      return <Utensils className="h-4 w-4 mr-2" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 relative">
      <div className="relative mb-6 search-bar">
        <div className="search-icon">
          <Search className="h-5 w-5" />
        </div>
        <input
          type="text"
          placeholder={t('menu.search')}
          value={searchQuery}
          onChange={handleSearch}
          className="search-input"
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {!searchActive && (
        <div className="relative mb-6">
          <div 
            ref={mainCategoriesRef}
            className="mb-2 overflow-x-auto category-scroll flex space-x-2 pb-3 px-4 scrollbar-hide"
            style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' as 'none' }}
          >
            {mainCategories.map((category) => (
              <button
                key={category.id}
                data-category-id={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors flex items-center
                  ${selectedCategoryId === category.id 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                {getCategoryIcon(category.name)}
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div 
        ref={contentRef}
        className="overflow-y-auto max-h-[calc(100vh-220px)] pr-2"
        style={{ scrollBehavior: 'smooth' }}
      >
        {!searchActive ? (
          groupedItemsList.map((group) => (
            <div 
              key={group.categoryId}
              ref={(el) => {
                if (el) sectionsRef.current.set(group.categoryId, el);
              }}
              data-category-id={group.categoryId}
              className="mb-8"
            >
              <div className="sticky top-0 z-10 bg-white shadow-md py-3 px-4 mb-4 rounded-md flex items-center justify-between">
                <div className="flex items-center">
                  {getCategoryIcon(group.categoryName)}
                  <h3 className="text-lg font-medium text-gray-900">{group.categoryName}</h3>
                </div>
                <span className="text-sm text-gray-500">{group.items.length} {t('items')}</span>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {group.items.map(item => (
                  <MenuItemCard 
                    key={item.id} 
                    item={item} 
                    storeCurrency={storeCurrency} 
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          products.filter(item => 
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
          ).map(item => (
            <MenuItemCard 
              key={item.id} 
              item={item} 
              storeCurrency={storeCurrency} 
            />
          ))
        )}
        
        {groupedItemsList.length === 0 && !searchActive && (
          <div className="py-12 px-4 text-center empty-state">
            <Utensils className="h-12 w-12 mx-auto text-gray-400" />
            <h3 className="text-lg font-medium text-gray-700">{t('menu.noProductsFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('menu.noCategoryProducts')}</p>
          </div>
        )}
      </div>

      {showScrollTop && !searchActive && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-6 bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all duration-300 hover:bg-blue-700 z-10"
          aria-label="Scroll to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}

     

      {selectedCategoryId && !searchActive && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full shadow-md text-sm font-medium z-10 flex items-center">
          {getCategoryIcon(productGroups.find(cat => cat.id === selectedCategoryId)?.name || '')}
          <span>{productGroups.find(cat => cat.id === selectedCategoryId)?.name}</span>
        </div>
      )}
    </div>
  );
};

export default EnhancedMenuContainer;